import { components } from '@orama/orama';
import type { Orama } from '@orama/orama';

import { allCollections } from '@flintmint/core-shared/constants';

export const oramaSchema = {
  id: 'string',
  type: 'string',
  hasPendingReview: 'boolean',
  sourceKind: 'enum',
  availabilityScope: 'enum',
  name: 'string',
  content: 'string',
  parsedContent: 'string',
  aliases: 'string[]',
  tagIds: 'enum[]',
  tagNames: 'string[]',
  numTags: 'number',
  createdAt: 'number',
  updatedAt: 'number',
  archivedAt: 'number',
} as const;

// Every value the indexed `type` field can take: one per content collection, plus
// 'templates' (notes carrying variant 'templates'). Anything that facets or counts by type
// must size itself from this list, not from a hand-kept number.
export const searchableTypes = [...allCollections, 'templates'] as const;

// Construction-time config for the worker's index, kept beside the schema because it feeds
// the persisted-index version below.
export const oramaUnsortableProperties: string[] = ['id'];

// Derived from schema keys+values - automatically invalidates persisted index when schema changes
export const oramaSchemaVersion = Object.entries(oramaSchema)
  .sort()
  .map(([k, v]) => `${k}:${v}`)
  .join(',');

// Version the persisted-index keys are built from. load() replaces the saved index state
// wholesale (data.sorting included) and never re-applies construction-time config, so ANY
// such config must be part of this string: without it a changed sort config would restore a
// blob the running index disagrees with, and e.g. sorting on a newly sortable field throws
// until the next rebuild.
export const oramaIndexVersion = `${oramaSchemaVersion},unsortable:${[...oramaUnsortableProperties].sort().join('|')}`;

// The search worker builds its index with Orama's DEFAULT tokenizer (and load() restores
// only the language onto the default tokenizer of a fresh index), so this factory
// MUST stay equivalent to the default English tokenizer. It exists so main-thread
// token logic (name promotion in use-search) provably matches the index.
export const createSearchTokenizer = () => components.tokenizer.createTokenizer({ language: 'english' });

export type OramaDBType = Orama<typeof oramaSchema>;

// Explicit type to avoid issues with Orama's complex generics (tagIds enum[], updatedAt number)
export type SearchableDocument = {
  id: string;
  type: string;
  hasPendingReview?: boolean;
  sourceKind?: 'image' | 'audio' | 'other';
  availabilityScope?: 'global' | 'note';
  name: string;
  content: string;
  parsedContent?: string;
  aliases: string[];
  tagIds: string[];
  tagNames: string[];
  numTags: number;
  createdAt: number;
  updatedAt: number;
  // Present only on archived docs; the Archive view renders it as the deletion date.
  archivedAt?: number;
  sourceType?: string;
  error?: string;
  source?: string;
  syncStatus?: string;
};
